import React from "react";
import { PageBuilderDescriptionTextFragment } from "types/graphql-types";
import { SlideshowProps } from "components/Slideshow/Slideshow";
import { graphql } from "gatsby";
import TextBox from "components/TextBox/TextBox";
import PageBuilderButtonCTA from "./PageBuilderButtonCTA";
import {
  ButtonsContainer,
  BackgroundColor,
  DescriptionText,
  DescriptionTextColumn,
  DescriptionTextColumnContent,
  DescriptionTextColumnContentLeft,
  DescriptionTextColumnContentRight,
  DescriptionTextColumnIconHighlights,
  DescriptionTextColumnLeadIn,
  DescriptionTextColumnTitle,
  DescriptionTextContent,
  DescriptionTextLeadIn,
  IconName,
  HeadingType
} from "@silversea-ssc/ui-components-react";
import { isPresent } from "src/utils/checks";
import { match } from "ts-pattern";
const ResponsiveSlideshow = React.lazy(
  () => import("components/Slideshow/ResponsiveSlideshow")
);

type Props = React.PropsWithChildren<
  PageBuilderDescriptionTextFragment & {
    headingType: HeadingType;
    //oppsiteColor?: BackgroundColor | "neutral60";
    backgroundColor: BackgroundColor;
  }
>;

const PageBuilderDescriptionText: React.FC<Props> = (props) => {
  const { slideshow } = props;
  const filteredSlideshow = slideshow?.filter(isPresent);

  if (filteredSlideshow && filteredSlideshow.length > 0) {
    return (
      <PageBuilderDescriptionTextWithSlideshow
        {...props}
        slideshow={filteredSlideshow}
      />
    );
  } else {
    return <PageBuilderDescriptionTextNoSlideshow {...props} />;
  }
};

export default PageBuilderDescriptionText;

const PageBuilderDescriptionTextNoSlideshow: React.FC<Props> = ({
  content,
  headingType,
  ctaButtons
}) => {
  return (
    <DescriptionText>
      {content?.leadIn && (
        <DescriptionTextLeadIn headingType={headingType} key="leadIn">
          {content.leadIn.localized}
        </DescriptionTextLeadIn>
      )}
      {(content?.content || (ctaButtons && ctaButtons?.length > 0)) && (
        <DescriptionTextContent key="content">
          {content?.content && (
            <TextBox fontSize="medium" content={content.content.localized} />
          )}
          {ctaButtons && ctaButtons.length > 0 && (
            <ButtonsContainer align="left" mobileFullSize spacingTop="regular">
              {ctaButtons.map((b) => (
                <PageBuilderButtonCTA key={b?._key} {...b} />
              ))}
            </ButtonsContainer>
          )}
        </DescriptionTextContent>
      )}
    </DescriptionText>
  );
};

const PageBuilderDescriptionTextWithSlideshow: React.FC<
  Props & { slideshow: NonNullable<NonNullable<Props["slideshow"]>[number]>[] }
> = ({
  content,
  slideshow,
  ctaButtons,
  variant,
  backgroundColor,
  highlights
}) => {
  const isBigTitle = variant == "bigTitle";
  const slideshowVariant = match<
    BackgroundColor,
    NonNullable<SlideshowProps["variant"]>
  >(backgroundColor)
    .with("titanium", () => "dark")
    .with("white", () => "gray")
    .otherwise(() => "white");

  const filteredHighlights = (highlights?.filter(isPresent) || []).map((x) => ({
    key: x._key!,
    label: x.label?.localized || "",
    icon: x.icon as IconName
  }));

  return (
    <DescriptionTextColumn>
      {content?.leadIn && !isBigTitle && (
        <DescriptionTextColumnLeadIn>
          {content.leadIn.localized}
        </DescriptionTextColumnLeadIn>
      )}

      {content?.content && (
        <DescriptionTextColumnContent>
          <DescriptionTextColumnContentLeft>
            {content?.leadIn && isBigTitle && (
              <DescriptionTextColumnTitle>
                {content.leadIn.localized}
              </DescriptionTextColumnTitle>
            )}
            {filteredHighlights.length > 0 && (
              <DescriptionTextColumnIconHighlights items={filteredHighlights} />
            )}

            <TextBox fontSize="medium" content={content.content.localized} />
            {ctaButtons && ctaButtons.length > 0 && (
              <ButtonsContainer
                align="left"
                mobileFullSize
                spacingTop="regular"
              >
                {ctaButtons.map((b) => (
                  <PageBuilderButtonCTA key={b?._key} {...b} />
                ))}
              </ButtonsContainer>
            )}
          </DescriptionTextColumnContentLeft>

          <DescriptionTextColumnContentRight>
            <React.Suspense>
              <ResponsiveSlideshow
                items={slideshow}
                variant={slideshowVariant}
                dataContentName="slideshow"
              />
            </React.Suspense>
          </DescriptionTextColumnContentRight>
        </DescriptionTextColumnContent>
      )}
    </DescriptionTextColumn>
  );
};

export const fragments = graphql`
  fragment PageBuilderDescriptionText on SanityDescriptionText {
    _key
    variant
    sectionLayoutConfig {
      ...SectionLayoutConfig
    }
    content {
      leadIn {
        localized
      }
      content {
        localized
      }
    }
    highlights {
      _key
      icon
      label {
        localized
      }
    }
    slideshow {
      ...SlideshowWithVideo
    }

    ctaButtons {
      ...ButtonCTA
    }
  }
`;
